import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Seo from "@/components/Seo";
import HomeSocialProof from "@/components/HomeSocialProof";
import CategoryChipStrip from "@/components/CategoryChipStrip";
import RecentlyViewed from "@/components/RecentlyViewed";
import { getRecentlyViewed } from "@/lib/recentlyViewed";
import SmartImage from "@/components/SmartImage";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";

const PRESS = ["FORBES", "BUSINESS INSIDER", "ENTREPRENEUR", "NETWORK18", "TECHCRUNCH", "VOGUE"];

const BANNERS = [
  {
    tag: "MEGA SALE",
    title: "MIN 50% OFF",
    subtitle: "on Luxury Handbags · Totes · Shoulder · Crossbody",
    cta_text: "Shop Handbags",
    cta_link: "/category/a-handbags",
    image: "https://images.unsplash.com/photo-1591561954557-26941169b49e?w=2000&q=85&auto=format&fit=crop",
    bg: "#ff3f6c",
  },
  {
    tag: "SNEAKER FEST",
    title: "40-70% OFF",
    subtitle: "Icons of streetwear · Runners · Retros · Court",
    cta_text: "Shop Sneakers",
    cta_link: "/category/a-mens-sneakers",
    image: "https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=2000&q=85&auto=format&fit=crop",
    bg: "#282c3f",
  },
  {
    tag: "FRESH DROPS",
    title: "NEW IN. NOW",
    subtitle: "Handpicked luxury · Curated weekly · Direct from Milan",
    cta_text: "Explore New Arrivals",
    cta_link: "/new-arrivals/women",
    image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=2000&q=85&auto=format&fit=crop",
    bg: "#ff905a",
  },
  {
    tag: "WATCHES & JEWELS",
    title: "TIME · GLOW · GOLD",
    subtitle: "Automatic · Chronograph · Certified Pre-owned",
    cta_text: "Shop Watches",
    cta_link: "/category/mens-watches",
    image: "https://images.unsplash.com/photo-1523170335258-f5ed11844a49?w=2000&q=85&auto=format&fit=crop",
    bg: "#282c3f",
  },
];

const FALLBACK_CONFIG = {
  hero: {
    women: {
      title: "Womenswear",
      subtitle: "Handbags · Heels · Fragrance",
      cta_text: "Shop Women",
      cta_link: "/collections/women",
      image: "https://customer-assets-rejwkqb3.emergentagent.net/job_9201df35-92a3-4931-a9d2-7d615a8dd53c/artifacts/avkcv4jh_file_0000000001108211877d26d6e9d53930.png",
    },
    men: {
      title: "Menswear",
      subtitle: "Sneakers · Watches · Fragrance",
      cta_text: "Shop Men",
      cta_link: "/collections/men",
      image: "https://customer-assets-rejwkqb3.emergentagent.net/job_9201df35-92a3-4931-a9d2-7d615a8dd53c/artifacts/2sbr2vfv_file_00000000ff588211ba687cab8c04790d.png",
    },
  },
  tiles: [
    { title: "Handbags",       subtitle: "Totes · Shoulder · Crossbody",   link: "/category/a-handbags",      image: "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=1000&q=85&auto=format&fit=crop" },
    { title: "Heels & Flats",  subtitle: "Pumps · Loafers · Ballerinas",   link: "/category/women-footwear?footwear_type=Pumps,Loafers,Ballet%20Flats",    image: "https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=1000&q=85&auto=format&fit=crop" },
    { title: "Men's Sneakers", subtitle: "Runners · Retros · Court",       link: "/category/a-mens-sneakers", image: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=1000&q=85&auto=format&fit=crop" },
    { title: "Watches",        subtitle: "Automatic · Chronograph · Dress", link: "/category/mens-watches",    image: "https://images.unsplash.com/photo-1622434641406-a158123450f9?w=1000&q=85&auto=format&fit=crop" },
  ],
  editorial: {
    eyebrow: "Deal of the Day",
    title: "Timeless pieces, up to 70% off.",
    subtitle: "Handpicked from the world's most desired houses — priced to move.",
    cta_text: "Shop Now",
    cta_link: "/collections",
    image: "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=2000&q=80&auto=format&fit=crop",
  },
  just_in: {
    enabled: false,
    title: "Trending Now",
    subtitle: "Fresh drops, hand-picked this week.",
    cta_text: "View all",
    cta_link: "/new-arrivals/women",
    slugs: [],
  },
};

/* ------------------------------------------------------------------ */
/*  Banner carousel — Myntra homepage centerpiece                     */
/* ------------------------------------------------------------------ */
const BannerCarousel = () => {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % BANNERS.length);
    }, 4500);
    return () => clearInterval(timerRef.current);
  }, []);

  const jump = (delta) => {
    setIdx((i) => (i + delta + BANNERS.length) % BANNERS.length);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setIdx((v) => (v + 1) % BANNERS.length);
    }, 4500);
  };

  return (
    <section className="relative overflow-hidden bg-bg-2" data-testid="home-banner">
      <div className="relative w-full aspect-[3/2] sm:aspect-[16/9] md:aspect-[21/9] max-h-[560px]">
        {BANNERS.map((b, i) => (
          <Link
            key={i}
            to={b.cta_link}
            aria-hidden={i !== idx}
            tabIndex={i === idx ? 0 : -1}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === idx ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            data-testid={`banner-slide-${i}`}
            style={{ backgroundColor: b.bg }}
          >
            <img
              src={b.image}
              alt={b.title}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : "auto"}
              className="absolute inset-0 w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-transparent" />
            <div className="absolute inset-0 flex items-center px-6 md:px-16 lg:px-24">
              <div className="max-w-xl text-white">
                <span
                  className="inline-block bg-white text-ink text-[10px] md:text-xs font-extrabold uppercase tracking-widest px-3 py-1 mb-4"
                  style={{ letterSpacing: "0.14em" }}
                >
                  {b.tag}
                </span>
                <h2 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold leading-[0.95] mb-3 tracking-tight">
                  {b.title}
                </h2>
                <p className="text-sm md:text-lg font-medium mb-6 max-w-md text-white/90">
                  {b.subtitle}
                </p>
                <span
                  className="inline-flex items-center gap-2 bg-white text-ink font-bold text-xs md:text-sm uppercase px-6 py-3 tracking-wider hover:bg-pink hover:text-white transition-colors"
                  style={{ letterSpacing: "0.06em" }}
                >
                  {b.cta_text}
                  <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Prev/Next chevrons */}
      <button
        type="button"
        onClick={() => jump(-1)}
        aria-label="Previous banner"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-white items-center justify-center shadow-lg transition"
        data-testid="banner-prev"
      >
        <ChevronLeft className="w-5 h-5 text-ink" />
      </button>
      <button
        type="button"
        onClick={() => jump(1)}
        aria-label="Next banner"
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-white items-center justify-center shadow-lg transition"
        data-testid="banner-next"
      >
        <ChevronRight className="w-5 h-5 text-ink" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {BANNERS.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`banner-dot-${i}`}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? "w-8 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Split hero — Menswear / Womenswear                                */
/* ------------------------------------------------------------------ */
const HeroSplit = ({ women, men }) => (
  <section
    className="lux-container pt-4 md:pt-6"
    data-testid="hero-split"
  >
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
      {[
        { key: "women", data: women, testId: "hero-tile-women", flag: "50% OFF" },
        { key: "men", data: men, testId: "hero-tile-men", flag: "40% OFF" },
      ].map(({ key, data, testId, flag }) => (
        <Link
          key={key}
          to={data.cta_link || "/"}
          className="group relative block overflow-hidden aspect-[4/3] md:aspect-[16/10] bg-bg-2"
          data-testid={testId}
          aria-label={data.cta_text}
        >
          <img
            src={data.image}
            alt={data.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute top-4 left-4 md:top-6 md:left-6 z-10">
            <span className="bg-pink text-white text-[10px] md:text-xs font-extrabold uppercase tracking-widest px-3 py-1">
              {flag}
            </span>
          </div>
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 text-white">
            <p className="text-[11px] md:text-xs uppercase font-bold tracking-widest text-white/80 mb-3">
              {data.subtitle}
            </p>
            <span className="inline-flex items-center gap-1.5 bg-white text-ink text-xs md:text-sm font-bold uppercase tracking-wider px-4 py-2 group-hover:bg-pink group-hover:text-white transition-colors">
              {data.cta_text}
              <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
            </span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/*  Recent-Aware Hero — shown when the shopper has 3+ history items    */
/*  so returning visits feel personalised. Full-bleed card carousel    */
/*  auto-advancing every 5s, with a "Or shop women / men" fallback     */
/*  row underneath so the split hero remains one tap away.             */
/* ------------------------------------------------------------------ */
const ContinueHero = ({ items, women, men }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (items.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(t);
  }, [items.length]);
  const active = items[idx];
  return (
    <section className="lux-container pt-4 md:pt-6" data-testid="hero-continue">
      <Link
        to={`/product/${active.slug}`}
        className="group relative block overflow-hidden aspect-[3/4] md:aspect-[21/9] md:max-h-[460px] bg-bg-2 md:bg-white rounded"
        data-testid="hero-continue-card"
      >
        <img
          src={active.image}
          alt={active.name}
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 md:left-1/2 md:w-1/2 md:object-contain md:bg-white md:p-6 md:mix-blend-multiply"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent md:hidden" />
        <div className="absolute inset-x-0 bottom-0 p-6 md:inset-y-0 md:left-0 md:right-auto md:w-1/2 md:flex md:flex-col md:justify-center md:p-14 md:bg-ink text-white">
          <span className="inline-block bg-pink text-white text-[10px] md:text-xs font-extrabold uppercase tracking-widest px-3 py-1 mb-3">
            Continue where you left off
          </span>
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-extrabold leading-tight mb-3 line-clamp-2" data-testid="hero-continue-title">
            {active.name}
          </h2>
          <span className="inline-flex items-center gap-1.5 bg-white text-ink text-xs md:text-sm font-bold uppercase tracking-wider px-4 py-2 group-hover:bg-pink group-hover:text-white transition-colors">
            View again
            <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
          </span>
        </div>
        {/* Dots */}
        {items.length > 1 && (
          <div className="absolute bottom-3 right-4 md:left-14 md:right-auto md:bottom-6 flex gap-1.5" aria-hidden>
            {items.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? "bg-white w-4" : "bg-white/50"}`}
              />
            ))}
          </div>
        )}
      </Link>
      <div className="flex items-center justify-center gap-4 mt-4 text-xs" data-testid="hero-continue-fallback">
        <span className="text-neutral-500">Or start fresh:</span>
        <Link to={women?.cta_link || "/collections/women"} className="font-semibold text-pink hover:opacity-70 uppercase tracking-wider" data-testid="hero-continue-women">
          Women
        </Link>
        <span className="text-neutral-300">·</span>
        <Link to={men?.cta_link || "/collections/men"} className="font-semibold text-ink hover:opacity-70 uppercase tracking-wider" data-testid="hero-continue-men">
          Men
        </Link>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Category tiles — Myntra "Shop by Category" grid                   */
/* ------------------------------------------------------------------ */
const EditTiles = ({ tiles }) => (
  <section className="lux-container pt-10 md:pt-14" data-testid="home-edit-tiles">
    <div className="text-center mb-5 md:mb-7">
      <p className="text-[10px] md:text-[11px] font-bold uppercase tracking-widest text-ink-mute mb-1">
        Best Sellers
      </p>
      <h2 className="section-title">Shop by Category</h2>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
      {tiles.map((t, i) => (
        <Link
          key={`edit-tile-${i}`}
          to={t.link || "/"}
          className="group block bg-white"
          data-testid={`edit-tile-${i}`}
        >
          <div className="aspect-square overflow-hidden bg-bg-2">
            <SmartImage
              src={t.image}
              alt={t.title}
              width={800}
              height={800}
              eager={i < 2}
              sizes="(min-width:768px) 25vw, 50vw"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          <div className="pt-2.5">
            <p className="text-[14px] md:text-[15px] font-bold text-ink group-hover:text-pink transition-colors">
              {t.title}
            </p>
            {t.subtitle && (
              <p className="text-[11px] md:text-xs text-ink-mute mt-0.5">
                {t.subtitle}
              </p>
            )}
          </div>
        </Link>
      ))}
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/*  Editorial banner — Myntra "Deal of the Day" full-width strip      */
/* ------------------------------------------------------------------ */
const EditorialBanner = ({ editorial }) => (
  <section className="lux-container pt-10 md:pt-14" data-testid="home-editorial">
    <div className="relative overflow-hidden aspect-[4/5] sm:aspect-[16/9] md:aspect-[21/9] bg-ink">
      <img
        src={editorial.image}
        alt={editorial.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover opacity-70"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/40 to-transparent" />
      <div className="absolute inset-0 flex items-center px-6 md:px-16 lg:px-24">
        <div className="max-w-xl text-white">
          {editorial.eyebrow && (
            <span className="inline-block bg-pink text-white text-[10px] md:text-xs font-extrabold uppercase tracking-widest px-3 py-1 mb-4">
              {editorial.eyebrow}
            </span>
          )}
          <h2 className="text-3xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-4">
            {editorial.title}
          </h2>
          {editorial.subtitle && (
            <p className="text-sm md:text-base text-white/85 mb-6 max-w-md leading-relaxed">
              {editorial.subtitle}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/collections/women"
              className="inline-flex items-center justify-center gap-2 bg-pink text-white font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-pink-dark transition-colors"
              data-testid="editorial-cta-women"
            >
              Shop Womenswear
              <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
            <Link
              to="/collections/men"
              className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white text-white font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-white hover:text-ink transition-colors backdrop-blur"
              data-testid="editorial-cta-men"
            >
              Shop Menswear
              <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ */
/*  Trending / Just-In carousel — horizontal snap                     */
/* ------------------------------------------------------------------ */
const JustInCarousel = ({ config, items }) => {
  if (!config?.enabled || !items || items.length === 0) return null;
  return (
    <section className="lux-container pt-10 md:pt-14" data-testid="home-just-in">
      <div className="text-center mb-5 md:mb-7">
        <p className="text-[10px] md:text-[11px] font-bold uppercase tracking-widest text-ink-mute mb-1">
          New this week
        </p>
        <h2 className="section-title">{config.title}</h2>
        {config.subtitle && (
          <p className="text-sm text-ink-mute mt-2 max-w-lg mx-auto">
            {config.subtitle}
          </p>
        )}
      </div>
      <div className="flex gap-3 md:gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-4 md:-mx-0 px-4 md:px-0 no-scrollbar">
        {items.map((p) => (
          <div
            key={p.id || p.slug}
            className="snap-start shrink-0 w-[42%] sm:w-[30%] md:w-[19%] lg:w-[15%]"
            data-testid={`just-in-item-${p.slug}`}
          >
            <ProductCard product={p} />
          </div>
        ))}
      </div>
      {config.cta_link && (
        <div className="mt-6 text-center">
          <Link
            to={config.cta_link}
            className="btn-outline inline-flex items-center gap-2"
            data-testid="just-in-cta"
          >
            {config.cta_text || "View all"}
            <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
          </Link>
        </div>
      )}
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Refer strip — Myntra pink banner                                  */
/* ------------------------------------------------------------------ */
const ReferStrip = () => (
  <section className="lux-container pt-10 md:pt-14">
    <Link
      to="/wallet"
      className="relative group block bg-pink text-white overflow-hidden"
      data-testid="home-refer-earn-strip"
      aria-label="Refer & Earn 20% commission"
    >
      <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-8 px-5 py-6 md:py-7 text-center md:text-left">
        <span className="text-[10px] md:text-xs uppercase font-bold tracking-widest text-white/80">
          Invite Only
        </span>
        <span className="text-xl md:text-3xl font-extrabold tracking-tight">
          Refer &amp; Earn <span className="text-yellowbrand">20%</span>
        </span>
        <span className="text-xs md:text-sm text-white/85 md:ml-2">
          on every friend&apos;s first order
        </span>
        <span className="inline-flex items-center gap-1.5 bg-white text-pink font-bold text-xs md:text-sm uppercase tracking-wider px-4 py-2 group-hover:bg-ink group-hover:text-white transition-colors">
          Get Your Link
          <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
        </span>
      </div>
    </Link>
  </section>
);

/* ------------------------------------------------------------------ */
/*  Main                                                              */
/* ------------------------------------------------------------------ */
const Home = ({ categories = [] }) => {
  const [config, setConfig] = useState(FALLBACK_CONFIG);
  const [justInItems, setJustInItems] = useState([]);
  // History-aware hero: swap the static split for a "Continue where
  // you left off" carousel once the shopper has viewed at least one
  // product (was gated at 3+ — that rarely triggers on mobile, where a
  // session is shorter, so most phones only ever saw the static Women/Men
  // banner). Reads localStorage once on mount so SSR + first paint agree
  // on which hero renders.
  const [recentHero, setRecentHero] = useState([]);
  useEffect(() => {
    try {
      const items = getRecentlyViewed()
        .filter((p) => p?.slug && p?.image)
        .slice(0, 6);
      if (items.length >= 1) setRecentHero(items);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    let alive = true;
    const isDraft = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("draft-homepage") === "1";
    const fetchConfig = () => {
      // Draft mode: admins load the pending draft via the authenticated
      // endpoint. If they aren't admin, the fetch 401s and we silently
      // fall back to the public live config below.
      if (isDraft) {
        import("@/context/AuthContext").then(({ authClient }) => {
          authClient.get("/admin/homepage-config/draft")
            .then((r) => alive && r.data && setConfig(r.data))
            .catch(() => api.homepageConfig().then((d) => alive && d?.hero && setConfig(d)).catch(() => {}));
        });
        return;
      }
      api
        .homepageConfig()
        .then((data) => { if (alive && data && data.hero) setConfig(data); })
        .catch(() => { /* keep fallback */ });
    };
    fetchConfig();
    const onBust = () => fetchConfig();
    const onStorage = (e) => { if (e.key === "lux_homepage_v") fetchConfig(); };
    window.addEventListener("luxsova:homepage-updated", onBust);
    window.addEventListener("storage", onStorage);
    return () => {
      alive = false;
      window.removeEventListener("luxsova:homepage-updated", onBust);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    const slugs = config?.just_in?.slugs || [];
    if (!config?.just_in?.enabled || slugs.length === 0) {
      setJustInItems([]);
      return;
    }
    let alive = true;
    api
      .productsBySlugs(slugs)
      .then((data) => {
        if (alive) setJustInItems(data?.items || []);
      })
      .catch(() => {
        if (alive) setJustInItems([]);
      });
    return () => {
      alive = false;
    };
  }, [config?.just_in?.enabled, (config?.just_in?.slugs || []).join(",")]);

  return (
    <>
      <Seo />
      <BannerCarousel />
      <CategoryChipStrip categories={categories} />
      {recentHero.length >= 1 ? (
        <ContinueHero items={recentHero} women={config.hero.women} men={config.hero.men} />
      ) : (
        <HeroSplit women={config.hero.women} men={config.hero.men} />
      )}
      <HomeSocialProof />
      <JustInCarousel config={config.just_in} items={justInItems} />
      <EditTiles tiles={config.tiles} />
      <EditorialBanner editorial={config.editorial} />

      {/* Press marquee */}
      <section
        className="py-10 md:py-14 border-y border-line mt-10 md:mt-14 bg-white"
        style={{ contentVisibility: "auto", containIntrinsicSize: "160px" }}
      >
        <div className="lux-container">
          <p className="text-[10px] font-bold uppercase tracking-widest text-ink-mute text-center mb-5">
            As Featured In
          </p>
        </div>
        <div className="marquee">
          <div className="marquee-track">
            {[...PRESS, ...PRESS, ...PRESS].map((p, i) => (
              <span
                key={i}
                className="text-xl md:text-2xl font-extrabold text-ink-mute tracking-widest whitespace-nowrap"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      <RecentlyViewed title="Recently viewed" />
    </>
  );
};

export default Home;
